<!--
  Narrow screens get the stacked list instead of columns: same rows, same column
  set (so a hidden column stays hidden), same projected cell templates — see
  TrListComponent. Only one of the two layouts is ever in the DOM, so neither pays
  for the other's rendering.
-->
<tablesready-tr-list
  *ngIf="listMode; else columnLayout"
  [rows]="rows"
  [columns]="displayColumns"
  [visibleKeys]="visibleKeys"
  [cellTemplates]="cellTemplates"
  [groupDir]="groupDir"
  [groupBy]="groupBy"
  [dataKey]="dataKey"
  [trackBy]="trackBy"
  [viewportHeight]="viewportHeight"
  [hasMore]="hasMore"
  [emptyMessage]="emptyMessage"
  [rowId]="rowId"
  [rowClass]="rowClass"
  [rowHidden]="rowHidden"
  [highlightedId]="highlightedId"
  (loadMore)="loadMore.emit()"
></tablesready-tr-list>

<!-- List mode has no <table> to print from; see preparePrint(). -->
<div *ngIf="listMode && printSource" class="tr-print-source" aria-hidden="true">
  <ng-container *ngTemplateOutlet="columnLayout"></ng-container>
</div>

<ng-template #columnLayout>
<p-table
  [value]="rows"
  [dataKey]="dataKey"
  [rowTrackBy]="trackByRow"
  [scrollable]="scrollable"
  [scrollHeight]="viewportHeight"
  styleClass="tr-table"
  [size]="displayCompact ? 'small' : undefined"
>
  <ng-template pTemplate="header">
    <tr>
      <th
        *ngFor="let col of displayColumns; trackBy: trackByColKey"
        [class.tr-frozen]="col.pin === 'start'"
        [class.tr-frozen-end]="col.pin === 'end'"
        [class.tr-frozen--last]="col.pin === 'start' && col.key === lastStartKey"
        [class.tr-frozen-end--first]="col.pin === 'end' && col.key === firstEndKey"
        [class.tr-col--dragging]="dragColKey === col.key"
        [class.tr-col--dragover]="dragOverKey === col.key"
        [ngClass]="col.headerClass"
        [style.left.px]="col.pin === 'start' ? (frozenLeft[col.key] || 0) : null"
        [style.right.px]="col.pin === 'end' ? (frozenRight[col.key] || 0) : null"
        [style.width.px]="col.width"
        [style.minWidth.px]="col.width"
        [style.maxWidth.px]="col.width"
        class="tr-col"
        [attr.data-col]="col.key"
        [attr.data-testid]="'tr-col-' + col.key"
        (pointerdown)="onHeaderPointerDown($event, col)"
      >
        <div class="tr-col__inner">
          <ng-container *ngIf="headerTemplates.get(col.templateKey || col.key) as headerDir; else defaultHeader">
            <ng-container
              *ngTemplateOutlet="headerDir.template; context: { $implicit: col }"
            ></ng-container>
          </ng-container>
          <ng-template #defaultHeader>
            <tablesready-sortable-column
              *ngIf="col.sortKey; else plainLabel"
              [sortBy]="sortBy"
              [column]="col.sortKey"
              [title]="col.header"
              [orderAscending]="sortAscending"
              [showInactiveIcon]="false"
              (orderBy)="onOrderBy($event)"
            ></tablesready-sortable-column>
            <ng-template #plainLabel>
              <span class="tr-col__label">{{ col.header }}</span>
            </ng-template>
          </ng-template>

          <button
            *ngIf="layoutEditing && col.menu !== false"
            type="button"
            class="tr-col__menu hide-on-print"
            aria-label="Column options"
            data-testid="tr-col-menu"
            (click)="openColumnMenu($event, col)"
          >
            <i class="bi bi-three-dots-vertical"></i>
          </button>
        </div>

        <span
          *ngIf="resizableColumns && col.resizable !== false"
          class="tr-col__resizer hide-on-print"
          data-testid="tr-col-resizer"
          (pointerdown)="startResize($event, col)"
        >
          <i class="bi bi-grip-vertical"></i>
        </span>
      </th>
    </tr>
  </ng-template>

  <ng-template pTemplate="body" let-row let-i="rowIndex">
    <tr *ngIf="groupDir && isGroupBoundary(i)" class="tr-group-row" data-testid="tr-group-row">
      <td [attr.colspan]="displayColumns.length || 1">
        <ng-container
          *ngTemplateOutlet="groupDir.template; context: { $implicit: row, groupKey: groupKeyAt(i), index: i }"
        ></ng-container>
      </td>
    </tr>

    <tr
      [attr.id]="rowIdFor(row)"
      [ngClass]="rowClassFor(row)"
      [class.party-highlight]="isHighlighted(row)"
      [class.tr-row--hidden]="isRowHidden(row)"
      data-testid="tr-row"
    >
      <td
        *ngFor="let col of displayColumns; trackBy: trackByColKey"
        [attr.data-col]="col.key"
        [attr.data-testid]="'tr-cell-' + col.key"
        [class.tr-frozen]="col.pin === 'start'"
        [class.tr-frozen-end]="col.pin === 'end'"
        [class.tr-frozen--last]="col.pin === 'start' && col.key === lastStartKey"
        [class.tr-frozen-end--first]="col.pin === 'end' && col.key === firstEndKey"
        [ngClass]="col.cellClass"
        [style.left.px]="col.pin === 'start' ? (frozenLeft[col.key] || 0) : null"
        [style.right.px]="col.pin === 'end' ? (frozenRight[col.key] || 0) : null"
        [style.width.px]="col.width"
        [style.minWidth.px]="col.width"
        [style.maxWidth.px]="col.width"
      >
        <ng-container *ngIf="cellTemplates.get(col.templateKey || col.key) as cellDir; else defaultCell">
          <ng-container
            *ngTemplateOutlet="
              cellDir.template;
              context: { $implicit: row, index: i, column: col, visibleKeys: visibleKeys }
            "
          ></ng-container>
        </ng-container>
        <ng-template #defaultCell>{{ fieldValue(row, col.field) }}</ng-template>
      </td>
    </tr>
  </ng-template>

  <ng-template pTemplate="emptymessage">
    <tr data-testid="tr-empty-row">
      <td [attr.colspan]="displayColumns.length || 1" data-testid="tr-empty-message">{{ emptyMessage }}</td>
    </tr>
  </ng-template>
</p-table>
</ng-template>

<p-tieredMenu #colMenu [popup]="true" [model]="menuModel" appendTo="body"></p-tieredMenu>
